import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  Plus, Trash, House, Buildings, Info, Warning, CheckCircle, Calculator, PencilSimple,
  Sparkle, MapTrifold,
} from "@phosphor-icons/react";

/**
 * Pestaña Prexistencias — calcula el valor de reposición del continente
 * (y orientativamente del contenido) usando los módulos CYPE oficiales
 * por provincia, uso y calidad. Detecta infraseguro comparando con el
 * capital declarado en póliza.
 */
const fmtEUR = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
// Trunca a 2 decimales SIN redondear (788.249 → 788,24 €). Para precios €/m².
const fmtEUR2 = (n) => {
  const v = Math.trunc((Number(n) || 0) * 100) / 100;
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
};
const fmtPct = (n) => `${(n ?? 0).toLocaleString("es-ES", { maximumFractionDigits: 2 })}%`;

const CALIDAD_LABEL = { basica: "Básica", media: "Media", alta: "Alta" };

export default function PrexistenciasTab({ exp, onReload }) {
  const [provincias, setProvincias] = useState([]);
  const [provincia, setProvincia] = useState("");
  const [modulos, setModulos] = useState(null);          // { provincia, code, items:[] }
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tipoInmueble, setTipoInmueble] = useState(exp?.prexistencia_tipo_inmueble || "");

  // Sync con cambios externos del expediente (otros tabs, recarga)
  useEffect(() => {
    setTipoInmueble(exp?.prexistencia_tipo_inmueble || "");
  }, [exp?.prexistencia_tipo_inmueble]);

  const elementos = exp?.prexistencia_elementos || [];
  const capCont = exp?.prexistencia_capital_continente ?? "";
  const capContenido = exp?.prexistencia_capital_contenido ?? "";
  const derogaPct = exp?.prexistencia_derogacion_pct ?? "";
  const expProvincia = exp?.extracted_data?.provincia;

  // Categorías que aparecen en los módulos cargados (para decidir si mostrar
  // el selector de tipo de inmueble o no — los módulos CYPE no varían por
  // tipo, sólo la aseguradora).
  const tiposDisponibles = useMemo(() => {
    if (!modulos?.items?.length) return [];
    const set = new Set();
    for (const it of modulos.items) {
      if (it.categoria) set.add(it.categoria);
    }
    return Array.from(set);
  }, [modulos]);

  // Si solo hay 1 tipo (módulos CYPE), lo seleccionamos automáticamente
  // y el selector visible queda oculto.
  useEffect(() => {
    if (tiposDisponibles.length === 1 && tipoInmueble !== tiposDisponibles[0]) {
      setTipoInmueble(tiposDisponibles[0]);
    }
  }, [tiposDisponibles, tipoInmueble]);

  const cambiarTipoInmueble = async (nuevo) => {
    if (nuevo === tipoInmueble) return;
    setTipoInmueble(nuevo);
    try {
      await api.put(`/expedientes/${exp.id}`, { prexistencia_tipo_inmueble: nuevo || null });
    } catch (e) {
      // revertir UI optimista
      setTipoInmueble(tipoInmueble);
      // eslint-disable-next-line no-console
      console.error("No se pudo guardar el tipo de inmueble", e);
    }
  };

  // Carga inicial de provincias
  useEffect(() => {
    api.get("/expedientes/modulos-cype/provincias")
      .then((r) => setProvincias(r.data.provincias || []))
      .catch(() => setProvincias([]));
  }, []);

  // Auto-selección: resuelve el nombre de la provincia del parte (ej "Barcelona")
  // contra la lista cargada y lo convierte al código ('08') esperado por el select.
  useEffect(() => {
    if (provincia || !expProvincia || provincias.length === 0) return;
    const norm = (s) => (s || "").toUpperCase()
      .normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
    const target = norm(expProvincia);
    const aliases = { "BIZKAIA": "VIZCAYA", "ARABA": "ALAVA", "GIPUZKOA": "GUIPUZCOA" };
    const aliased = aliases[target] || target;
    const hit = provincias.find((p) => norm(p.nombre) === aliased
      || norm(p.nombre).includes(aliased) || aliased.includes(norm(p.nombre)));
    if (hit) setProvincia(hit.code);
  }, [provincias, expProvincia, provincia]);

  // Resolver provincia → módulos (o módulos custom de aseguradora si están activos)
  useEffect(() => {
    // Si la aseguradora del expediente tiene módulos propios activos, los items
    // vienen sin filtrar por provincia. Si no, requerimos provincia.
    api.get(`/expedientes/modulos-cype/items`,
            { params: { provincia: provincia || undefined, expediente_id: exp?.id } })
      .then((r) => setModulos(r.data))
      .catch(() => setModulos(null));
  }, [provincia, exp?.id]);

  const resumen = useMemo(() => {
    let cont = 0, contenido = 0;
    for (const e of elementos) {
      const c = (e.metros || 0) * (e.precio_unitario || 0);
      cont += c;
      contenido += c * ((e.contenido_pct || 0) / 100);
    }
    return {
      total_continente: cont,
      total_contenido: contenido,
      total: cont + contenido,
    };
  }, [elementos]);

  const infraCont = useMemo(() => {
    if (capCont === "" || capCont === null) return null;
    const cap = Number(capCont) || 0;
    if (resumen.total_continente <= 0) return null;
    const ratio = cap / resumen.total_continente;
    const deficit_pct = Math.max(0, (1 - ratio) * 100);
    const deroga = derogaPct !== "" && derogaPct !== null ? Number(derogaPct) : null;
    const derogada = deroga !== null && deficit_pct <= deroga && cap < resumen.total_continente;
    return {
      hay: cap < resumen.total_continente && !derogada,
      derogada,
      ratio,
      deficit: resumen.total_continente - cap,
      deficit_pct,
      aplicable: derogada ? 100 : Math.min(100, ratio * 100),
      derogacion: deroga,
    };
  }, [capCont, resumen.total_continente, derogaPct]);

  const infraContenido = useMemo(() => {
    if (capContenido === "" || capContenido === null) return null;
    const cap = Number(capContenido) || 0;
    if (resumen.total_contenido <= 0) return null;
    const ratio = cap / resumen.total_contenido;
    const deficit_pct = Math.max(0, (1 - ratio) * 100);
    const deroga = derogaPct !== "" && derogaPct !== null ? Number(derogaPct) : null;
    const derogada = deroga !== null && deficit_pct <= deroga && cap < resumen.total_contenido;
    return {
      hay: cap < resumen.total_contenido && !derogada,
      derogada,
      ratio,
      deficit: resumen.total_contenido - cap,
      deficit_pct,
      aplicable: derogada ? 100 : Math.min(100, ratio * 100),
      derogacion: deroga,
    };
  }, [capContenido, resumen.total_contenido, derogaPct]);

  const addElemento = async (payload) => {
    await api.post(`/expedientes/${exp.id}/prexistencias/elementos`, {
      ...payload,
      provincia_code: modulos?.code,
      provincia_nombre: modulos?.provincia,
    });
    onReload?.();
    setAdding(false);
  };

  const updateElemento = async (id, patch) => {
    await api.put(`/expedientes/${exp.id}/prexistencias/elementos/${id}`, patch);
    onReload?.();
  };

  const deleteElemento = async (id) => {
    if (!window.confirm("¿Eliminar este elemento?")) return;
    await api.delete(`/expedientes/${exp.id}/prexistencias/elementos/${id}`);
    onReload?.();
  };

  const saveCapitales = async (patch) => {
    setSaving(true);
    try {
      await api.put(`/expedientes/${exp.id}/prexistencias/capitales`, patch);
      onReload?.();
    } finally { setSaving(false); }
  };

  const [readingParte, setReadingParte] = useState(false);
  const [parteMsg, setParteMsg] = useState(null);
  const readFromParte = async () => {
    setReadingParte(true); setParteMsg(null);
    try {
      const { data } = await api.post(`/expedientes/${exp.id}/prexistencias/read-from-parte`);
      if (data.updated) {
        setParteMsg({ ok: true, text: `Capitales leídos: continente ${fmtEUR(data.capital_continente)}${data.capital_contenido ? `, contenido ${fmtEUR(data.capital_contenido)}` : ""}.` });
        onReload?.();
      } else {
        setParteMsg({ ok: false, text: data.reason || "La IA no encontró capitales en el parte." });
      }
      setTimeout(() => setParteMsg(null), 6000);
    } catch (e) {
      setParteMsg({ ok: false, text: e?.response?.data?.detail || "Error leyendo el parte" });
    } finally { setReadingParte(false); }
  };

  const [fromCat, setFromCat] = useState(false);
  const [catMsg, setCatMsg] = useState(null);
  const catastroDisponible = !!(exp?.catastro_datos?.superficie_m2 && exp?.catastro_datos?.uso);
  const addFromCatastro = async () => {
    setFromCat(true); setCatMsg(null);
    try {
      const { data } = await api.post(`/expedientes/${exp.id}/prexistencias/from-catastro`);
      setCatMsg({ ok: true, text: `Elemento creado: ${data.elemento.concepto} · ${data.elemento.metros} m².` });
      onReload?.();
      setTimeout(() => setCatMsg(null), 5000);
    } catch (e) {
      setCatMsg({ ok: false, text: e?.response?.data?.detail || "No se pudo prellenar desde Catastro" });
    } finally { setFromCat(false); }
  };

  return (
    <div data-testid="prexistencias-tab" className="h-full overflow-y-auto bg-surface">
      <div className="p-6 space-y-6 max-w-6xl pb-16">
        {/* Header con total y provincia */}
        <header className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-5 shadow-lg ring-1 ring-emerald-400/40">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-emerald-400 font-bold mb-2">
                <Calculator className="w-4 h-4" weight="fill" />
                {modulos?.source === "aseguradora"
                  ? `Prexistencias · Valor de reposición (módulos ${modulos.aseguradora_nombre || "compañía"})`
                  : "Prexistencias · Valor de reposición (módulos CYPE)"}
              </div>
              <h2 className="font-display font-bold text-4xl tracking-tight tabular-nums text-white" data-testid="prex-total">
                {fmtEUR(resumen.total)}
              </h2>
              <div className="text-[12px] text-slate-300 mt-1">
                {elementos.length} elemento{elementos.length !== 1 ? "s" : ""} · Continente + contenido orientativo
              </div>
            </div>
            <div className="flex items-stretch gap-2 flex-wrap">
              <Pill label="Continente" value={fmtEUR(resumen.total_continente)} color="emerald" testid="prex-total-continente" />
              <Pill label="Contenido" value={fmtEUR(resumen.total_contenido)} color="sky" testid="prex-total-contenido" />
            </div>
          </div>
        </header>

        {/* Selector de provincia */}
        <section className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-[11px] uppercase tracking-widest font-bold text-muted">Provincia</label>
            <select
              value={provincia}
              onChange={(e) => setProvincia(e.target.value)}
              data-testid="prex-provincia-select"
              className="border border-border rounded-md px-3 py-1.5 text-sm bg-white min-w-[220px]"
            >
              <option value="">— Selecciona —</option>
              {provincias.map((p) => (
                <option key={p.code} value={p.code}>{p.code} · {p.nombre}</option>
              ))}
            </select>
            {modulos && modulos.code && modulos.source === "aseguradora" && (
              <span className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-2 py-1 inline-flex items-center gap-1 font-semibold">
                <Sparkle className="w-3 h-3" weight="fill" />
                Módulos propios de {modulos.aseguradora_nombre || "la compañía"} ({modulos.items.length} conceptos)
              </span>
            )}
            {modulos && modulos.code && modulos.source !== "aseguradora" && (
              <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1 inline-flex items-center gap-1">
                <CheckCircle className="w-3 h-3" weight="fill" /> CYPE · {modulos.items.length} conceptos
              </span>
            )}
            {provincia && !modulos?.code && (
              <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 inline-flex items-center gap-1">
                <Warning className="w-3 h-3" weight="fill" /> Provincia no reconocida. Selecciona una del desplegable.
              </span>
            )}
          </div>
          {/* Selector de tipo de inmueble (solo si los módulos varían por tipo,
              es decir cuando la aseguradora tiene >1 categoría) */}
          {tiposDisponibles.length > 1 && (
            <div className="flex items-center gap-2 flex-wrap mt-3 pt-3 border-t border-slate-100">
              <label className="text-[11px] uppercase tracking-widest font-bold text-muted">Tipo de inmueble</label>
              <div className="inline-flex border border-border rounded-md overflow-hidden bg-white">
                {tiposDisponibles.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => cambiarTipoInmueble(t)}
                    data-testid={`prex-tipo-${t.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`text-xs px-3 py-1.5 font-semibold transition-colors ${
                      tipoInmueble === t
                        ? "bg-emerald-600 text-white"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              {!tipoInmueble && (
                <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 inline-flex items-center gap-1">
                  <Warning className="w-3 h-3" weight="fill" /> Elige el tipo para que se aplique el precio correcto.
                </span>
              )}
            </div>
          )}
          <p className="text-[11px] text-muted mt-2">
            {modulos?.source === "aseguradora"
              ? `Los precios provienen del listado oficial de ${modulos.aseguradora_nombre || "la compañía"} por m². El valor de reposición se actualiza automáticamente conforme añades elementos.`
              : "Los precios provienen del listado oficial de módulos CYPE por m². El valor de reposición se actualiza automáticamente conforme añades elementos."}
          </p>
        </section>

        {/* Lista de elementos */}
        <section className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50 flex-wrap">
            <div className="font-display font-bold text-sm text-slate-900 tracking-tight">Elementos declarados</div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={addFromCatastro}
                disabled={!catastroDisponible || !modulos?.code || fromCat}
                data-testid="prex-from-catastro"
                title={catastroDisponible ? "Crea un elemento con el uso y superficie del Catastro" : "Primero consulta el Catastro en la pestaña Expediente"}
                className="inline-flex items-center gap-1 bg-white text-sky-700 border border-sky-300 text-[11px] font-semibold rounded-md px-2.5 py-1.5 hover:bg-sky-50 disabled:opacity-50"
              >
                <MapTrifold className="w-3.5 h-3.5" weight="bold" />
                {fromCat ? "Leyendo…" : "Prellenar desde Catastro"}
              </button>
              <button
                onClick={() => setAdding(true)}
                disabled={!modulos?.code}
                data-testid="prex-add-elemento"
                className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-[11px] font-semibold rounded-md px-2.5 py-1.5"
              >
                <Plus className="w-3.5 h-3.5" weight="bold" /> Añadir elemento
              </button>
            </div>
          </div>

          {catMsg && (
            <div data-testid="prex-catastro-msg" className={`px-4 py-2 text-[12px] border-b ${catMsg.ok ? "bg-sky-50 text-sky-800 border-sky-200" : "bg-red-50 text-red-700 border-red-200"}`}>
              {catMsg.ok ? "✅ " : "⚠ "}{catMsg.text}
            </div>
          )}

          {adding && modulos?.code && (
            <ElementoForm
              modulos={modulos}
              tipoInmueble={tipoInmueble}
              onCancel={() => setAdding(false)}
              onSave={addElemento}
            />
          )}

          {elementos.length === 0 && !adding && (
            <div className="text-center py-8 text-[12px] text-muted">
              Sin elementos. Añade el primer uso (vivienda, garaje, nave…) para calcular el valor prexistente.
            </div>
          )}

          {elementos.length > 0 && (
            <div className="divide-y divide-slate-100">
              <div className="hidden md:grid grid-cols-[1.7fr_0.7fr_0.6fr_0.6fr_0.6fr_0.8fr_auto] gap-2 px-4 py-2 text-[10px] uppercase tracking-widest font-bold text-muted bg-slate-50/60">
                <div>Concepto</div>
                <div>Calidad</div>
                <div className="text-right">m²</div>
                <div className="text-right">€/m²</div>
                <div className="text-right">% Contenido</div>
                <div className="text-right">Continente</div>
                <div className="w-8" />
              </div>
              {elementos.map((el) => (
                <ElementoRow
                  key={el.id}
                  elemento={el}
                  onUpdate={(patch) => updateElemento(el.id, patch)}
                  onDelete={() => deleteElemento(el.id)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Capitales declarados + infraseguro */}
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="font-display font-bold text-sm text-slate-900 tracking-tight">Capitales declarados en póliza</div>
            <button
              onClick={readFromParte}
              disabled={readingParte}
              data-testid="prex-read-parte"
              title="La IA releerá el parte original y rellenará los capitales"
              className="inline-flex items-center gap-1 bg-white text-amber-700 border border-amber-300 text-[11px] font-semibold rounded-md px-2.5 py-1.5 hover:bg-amber-50 disabled:opacity-60"
            >
              {readingParte ? (
                <Sparkle className="w-3.5 h-3.5 animate-pulse" weight="fill" />
              ) : (
                <Sparkle className="w-3.5 h-3.5" weight="bold" />
              )}
              {readingParte ? "Leyendo parte…" : "Leer capitales del parte (IA)"}
            </button>
          </div>
          {parteMsg && (
            <div data-testid="prex-parte-msg" className={`px-3 py-2 text-[12px] rounded-md border ${parteMsg.ok ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-red-50 text-red-700 border-red-200"}`}>
              {parteMsg.ok ? "✨ " : "⚠ "}{parteMsg.text}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CapitalCard
              label="Capital declarado · Continente"
              color="emerald"
              icon={Buildings}
              total={resumen.total_continente}
              capital={capCont}
              onSave={(v) => saveCapitales({ prexistencia_capital_continente: v })}
              saving={saving}
              infra={infraCont}
              testid="prex-capital-continente"
            />
            <CapitalCard
              label="Capital declarado · Contenido"
              color="sky"
              icon={House}
              total={resumen.total_contenido}
              capital={capContenido}
              onSave={(v) => saveCapitales({ prexistencia_capital_contenido: v })}
              saving={saving}
              infra={infraContenido}
              testid="prex-capital-contenido"
            />
          </div>
        </section>

        {/* Derogación de la regla proporcional por infraseguro */}
        <DerogacionCard
          value={derogaPct}
          onSave={(v) => saveCapitales({ prexistencia_derogacion_pct: v })}
          saving={saving}
          infraCont={infraCont}
          infraContenido={infraContenido}
        />
      </div>
    </div>
  );
}

// =====================================================================
// Pill (totales en cabecera)
// =====================================================================
function Pill({ label, value, color, testid }) {
  const c = color === "sky" ? "text-sky-300" : "text-emerald-300";
  return (
    <div className="bg-slate-800 rounded-lg px-3 py-2 border border-slate-700 min-w-[140px]">
      <div className={`text-[9px] uppercase tracking-widest font-bold ${c}`}>{label}</div>
      <div className="font-display font-bold text-base text-white tabular-nums" data-testid={testid}>{value}</div>
    </div>
  );
}

// =====================================================================
// Formulario de añadir elemento
// =====================================================================
function ElementoForm({ modulos, tipoInmueble, onCancel, onSave }) {
  // Filtrado robusto: aplicamos provincia (si los módulos varían por
  // provincia) y tipo de inmueble (si los módulos varían por tipo). Después
  // sólo deben quedar 4 sub-conceptos planos:
  //    Vivienda · Trastero · Garaje · Zonas comunes
  const itemsTipo = useMemo(() => {
    let items = modulos.items || [];
    // Filtrado defensivo por provincia: si el endpoint devolviera el
    // listado nacional completo (624), nos quedamos solo con los de la
    // provincia seleccionada en cabecera.
    if (modulos.code && modulos.code !== "ASEG") {
      const itemsConProv = items.filter((it) => it.provincia_code);
      if (itemsConProv.length > 0) {
        items = items.filter(
          (it) => !it.provincia_code || it.provincia_code === modulos.code,
        );
      }
    }
    if (tipoInmueble) {
      items = items.filter((it) => it.categoria === tipoInmueble);
    }
    return items;
  }, [modulos, tipoInmueble]);

  // Ordenamos los conceptos para el desplegable.
  // - Módulos de aseguradora: cada subcategoría es un concepto único
  //   (Vivienda · Trastero · Garaje · Zonas comunes) → dedupamos por
  //   subcategoría y aplicamos el orden estándar.
  // - Módulos CYPE: cada concepto es único (Unifamiliar aislada,
  //   Unifamiliar pareada, en hilera…) → NO dedupar por subcategoría;
  //   los listamos todos, agrupados por subcategoría.
  const ORDER_ASEG = ["Vivienda", "Trastero", "Garaje", "Zonas comunes"];
  const conceptos = useMemo(() => {
    if (modulos.source === "aseguradora") {
      const seen = new Set();
      const sorted = [];
      for (const it of itemsTipo) {
        const key = it.subcategoria || it.concepto;
        if (seen.has(key)) continue;
        seen.add(key);
        sorted.push(it);
      }
      sorted.sort(
        (a, b) =>
          ORDER_ASEG.indexOf(a.subcategoria || "") - ORDER_ASEG.indexOf(b.subcategoria || ""),
      );
      return sorted;
    }
    // CYPE: agrupados por subcategoría, ordenados alfabéticamente.
    return [...itemsTipo].sort((a, b) => {
      const sa = (a.subcategoria || "").localeCompare(b.subcategoria || "", "es");
      if (sa !== 0) return sa;
      return (a.concepto || "").localeCompare(b.concepto || "", "es");
    });
  }, [itemsTipo, modulos.source]);

  const [conceptoIdx, setConceptoIdx] = useState(0);
  const [calidad, setCalidad] = useState("media");
  const [metros, setMetros] = useState("");
  const [contenidoPct, setContenidoPct] = useState(30);

  // Si cambia el tipo de inmueble desde fuera, reseteamos el índice
  useEffect(() => {
    setConceptoIdx(0);
  }, [tipoInmueble]);

  const item = conceptos?.[conceptoIdx];
  const precio = item ? item[calidad] : 0;
  const continente = (Number(metros) || 0) * (precio || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!item || !metros) return;
    await onSave({
      concepto: item.concepto,
      categoria: item.categoria,
      subcategoria: item.subcategoria,
      calidad,
      metros: Number(metros),
      precio_unitario: precio,
      contenido_pct: Number(contenidoPct) || 0,
    });
  };

  // Si requiere tipo y no se ha elegido, mostramos un mensaje claro en lugar
  // del formulario.
  const tieneVariosTipos = useMemo(() => {
    const cats = new Set((modulos.items || []).map((it) => it.categoria).filter(Boolean));
    return cats.size > 1;
  }, [modulos]);

  if (!conceptos.length || (tieneVariosTipos && !tipoInmueble)) {
    return (
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-4 text-[13px] text-amber-800 flex items-center gap-2" data-testid="prex-elemento-form-empty">
        <Warning className="w-4 h-4 flex-shrink-0" weight="fill" />
        Elige primero el tipo de inmueble (arriba, junto a la provincia) para poder añadir elementos.
        <button onClick={onCancel} type="button" className="ml-auto text-xs text-amber-900 underline">Cancelar</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-emerald-50/40 border-b border-emerald-200 px-4 py-4 space-y-3" data-testid="prex-elemento-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted">Concepto</label>
          <select
            value={conceptoIdx}
            onChange={(e) => setConceptoIdx(Number(e.target.value))}
            className="w-full border border-border rounded-md px-2 py-1.5 text-sm bg-white mt-1"
            data-testid="prex-form-concepto"
          >
            {modulos.source === "aseguradora" ? (
              conceptos.map((it, idx) => (
                <option key={`${it.subcategoria || it.concepto}-${idx}`} value={idx}>
                  {it.subcategoria || it.concepto}
                </option>
              ))
            ) : (
              // CYPE: agrupado por subcategoría con <optgroup>
              Object.entries(
                conceptos.reduce((acc, it, idx) => {
                  const sub = it.subcategoria || "Otros";
                  (acc[sub] = acc[sub] || []).push({ it, idx });
                  return acc;
                }, {}),
              ).map(([sub, list]) => (
                <optgroup key={sub} label={sub}>
                  {list.map(({ it, idx }) => (
                    <option key={idx} value={idx}>
                      {it.concepto}
                    </option>
                  ))}
                </optgroup>
              ))
            )}
          </select>
          {tipoInmueble && (
            <p className="text-[10px] text-muted mt-1">
              Tipo: <span className="font-semibold text-slate-700">{tipoInmueble}</span>
            </p>
          )}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted">Calidad</label>
          <div className="mt-1 inline-flex border border-border rounded-md overflow-hidden bg-white">
            {["basica", "media", "alta"].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCalidad(c)}
                className={`px-3 py-1.5 text-xs font-semibold ${calidad === c ? "bg-emerald-600 text-white" : "text-slate-700 hover:bg-slate-50"}`}
                data-testid={`prex-form-calidad-${c}`}
              >
                {CALIDAD_LABEL[c]}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-muted mt-1">
            {item && <>B: {fmtEUR2(item.basica)} · M: {fmtEUR2(item.media)} · A: {fmtEUR2(item.alta)} /m²</>}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted">m²</label>
          <input
            type="number"
            min="0"
            step="0.1"
            value={metros}
            onChange={(e) => setMetros(e.target.value)}
            required
            autoFocus
            data-testid="prex-form-metros"
            className="w-full border border-border rounded-md px-2 py-1.5 text-sm bg-white mt-1"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted">€/m²</label>
          <div className="w-full border border-dashed border-slate-300 rounded-md px-2 py-1.5 text-sm bg-slate-50 mt-1 tabular-nums">
            {fmtEUR2(precio)}
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted">% contenido</label>
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            value={contenidoPct}
            onChange={(e) => setContenidoPct(e.target.value)}
            data-testid="prex-form-contenido-pct"
            className="w-full border border-border rounded-md px-2 py-1.5 text-sm bg-white mt-1"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-muted">Continente</label>
          <div className="w-full border border-dashed border-emerald-300 rounded-md px-2 py-1.5 text-sm bg-emerald-50 mt-1 text-emerald-800 font-semibold tabular-nums">
            {fmtEUR(continente)}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button
          type="submit"
          data-testid="prex-form-save"
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-md px-3 py-1.5"
        >
          Guardar elemento
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="text-xs text-muted hover:text-foreground px-2 py-1.5"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

// =====================================================================
// Fila de un elemento (inline-edit de m², calidad y %contenido)
// =====================================================================
function ElementoRow({ elemento, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [metros, setMetros] = useState(elemento.metros);
  const [calidad, setCalidad] = useState(elemento.calidad);
  const [contenidoPct, setContenidoPct] = useState(elemento.contenido_pct ?? 30);

  useEffect(() => {
    setMetros(elemento.metros);
    setCalidad(elemento.calidad);
    setContenidoPct(elemento.contenido_pct ?? 30);
  }, [elemento]);

  const continente = (elemento.metros || 0) * (elemento.precio_unitario || 0);

  const save = async () => {
    await onUpdate({
      metros: Number(metros) || 0,
      calidad,
      contenido_pct: Number(contenidoPct) || 0,
    });
    setEditing(false);
  };

  return (
    <div
      data-testid={`prex-elemento-${elemento.id}`}
      className="grid grid-cols-[1.7fr_0.7fr_0.6fr_0.6fr_0.6fr_0.8fr_auto] gap-2 px-4 py-3 items-center text-[13px] hover:bg-slate-50/60"
    >
      <div className="min-w-0">
        <div className="font-semibold text-slate-900 truncate">{elemento.concepto}</div>
        {elemento.subcategoria && <div className="text-[10px] text-muted truncate">{elemento.subcategoria}</div>}
      </div>

      {editing ? (
        <select
          value={calidad}
          onChange={(e) => setCalidad(e.target.value)}
          className="border border-border rounded-md px-1.5 py-1 text-xs"
        >
          <option value="basica">Básica</option>
          <option value="media">Media</option>
          <option value="alta">Alta</option>
        </select>
      ) : (
        <div className="text-xs">{CALIDAD_LABEL[elemento.calidad]}</div>
      )}

      {editing ? (
        <input
          type="number"
          value={metros}
          onChange={(e) => setMetros(e.target.value)}
          className="w-full border border-border rounded-md px-1.5 py-1 text-xs text-right tabular-nums"
        />
      ) : (
        <div className="text-right tabular-nums">{(elemento.metros || 0).toLocaleString("es-ES")}</div>
      )}

      <div className="text-right tabular-nums text-muted">{fmtEUR2(elemento.precio_unitario)}</div>

      {editing ? (
        <input
          type="number"
          value={contenidoPct}
          onChange={(e) => setContenidoPct(e.target.value)}
          className="w-full border border-border rounded-md px-1.5 py-1 text-xs text-right tabular-nums"
        />
      ) : (
        <div className="text-right tabular-nums text-muted">{fmtPct(elemento.contenido_pct ?? 30)}</div>
      )}

      <div className="text-right tabular-nums font-semibold text-slate-900">
        {fmtEUR(continente)}
      </div>

      <div className="flex items-center justify-end gap-1">
        {editing ? (
          <button
            onClick={save}
            className="text-[11px] bg-emerald-600 text-white font-semibold rounded-md px-2 py-1 hover:bg-emerald-700"
            data-testid={`prex-save-${elemento.id}`}
          >
            OK
          </button>
        ) : (
          <button
            onClick={() => setEditing(true)}
            className="text-muted hover:text-foreground p-1"
            title="Editar"
            data-testid={`prex-edit-${elemento.id}`}
          >
            <PencilSimple className="w-3.5 h-3.5" weight="bold" />
          </button>
        )}
        <button
          onClick={onDelete}
          className="text-muted hover:text-red-600 p-1"
          title="Eliminar"
          data-testid={`prex-delete-${elemento.id}`}
        >
          <Trash className="w-3.5 h-3.5" weight="bold" />
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Card de capital declarado + infraseguro
// =====================================================================
function CapitalCard({ label, color, icon: Icon, total, capital, onSave, saving, infra, testid }) {
  const [value, setValue] = useState(capital ?? "");

  useEffect(() => {
    setValue(capital ?? "");
  }, [capital]);

  const colorBar = color === "sky" ? "bg-sky-500" : "bg-emerald-500";
  const colorLabel = color === "sky" ? "text-sky-700" : "text-emerald-700";
  const colorBg = color === "sky" ? "bg-sky-50" : "bg-emerald-50";
  const colorBorder = color === "sky" ? "border-sky-200" : "border-emerald-200";

  const commit = () => {
    const v = value === "" ? null : Number(value);
    if (v !== capital) onSave(v);
  };

  return (
    <div className={`bg-white rounded-xl border ${colorBorder} p-4`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <div className={`w-8 h-8 rounded-lg ${colorBg} inline-flex items-center justify-center ${colorLabel}`}>
          <Icon className="w-4 h-4" weight="fill" />
        </div>
        <div>
          <div className="font-display font-bold text-sm text-slate-900 tracking-tight">{label}</div>
          <div className="text-[11px] text-muted">Valor prexistente calculado: <b className="tabular-nums">{fmtEUR(total)}</b></div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="number"
          min="0"
          step="100"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          placeholder="0"
          data-testid={`${testid}-input`}
          className="flex-1 border border-border rounded-md px-3 py-2 text-sm tabular-nums text-right bg-white"
        />
        <span className="text-xs text-muted">€</span>
        {saving && <span className="text-[10px] text-muted">guardando…</span>}
      </div>

      {/* Infraseguro */}
      {infra && total > 0 && (
        <div className="mt-3 space-y-2">
          <div className="relative h-2 bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`absolute top-0 left-0 h-full ${colorBar} transition-all`}
              style={{ width: `${Math.min(100, infra.ratio * 100)}%` }}
            />
          </div>
          {infra.hay ? (
            <div className="flex items-start gap-2 text-[11px] bg-red-50 border border-red-200 rounded-md p-2 text-red-800">
              <Warning className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" weight="fill" />
              <div>
                <b>Infraseguro detectado.</b> El capital declarado cubre solo el {fmtPct(infra.aplicable)}
                {infra.deficit_pct != null && <> (déficit del {fmtPct(infra.deficit_pct)})</>}.
                {' '}Déficit absoluto: <b className="tabular-nums">{fmtEUR(infra.deficit)}</b>.
                Aplica regla proporcional: el daño del continente se reduce a ese porcentaje.
              </div>
            </div>
          ) : infra.derogada ? (
            <div className="flex items-start gap-2 text-[11px] bg-amber-50 border border-amber-300 rounded-md p-2 text-amber-900">
              <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" weight="fill" />
              <div>
                <b>Regla proporcional derogada.</b> Hay déficit del {fmtPct(infra.deficit_pct)} pero
                la póliza deroga el infraseguro hasta el {fmtPct(infra.derogacion)} → se indemniza el 100%.
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 text-[11px] bg-emerald-50 border border-emerald-200 rounded-md p-2 text-emerald-800">
              <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" weight="fill" />
              <div>Sin infraseguro. El capital declarado cubre el valor prexistente.</div>
            </div>
          )}
        </div>
      )}
      {!infra && total > 0 && (
        <div className="mt-3 text-[11px] text-muted inline-flex items-start gap-1.5">
          <Info className="w-3 h-3 mt-0.5" weight="bold" />
          Introduce el capital declarado en póliza para detectar infraseguro.
        </div>
      )}
    </div>
  );
}



// =====================================================================
// Tarjeta «Derogación de la regla proporcional por infraseguro»
// =====================================================================
function DerogacionCard({ value, onSave, saving, infraCont, infraContenido }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);

  const commit = () => {
    const next = v === "" ? null : Number(v);
    if (next !== value) onSave(next);
  };

  const totalDef = (infraCont?.hay ? 1 : 0) + (infraContenido?.hay ? 1 : 0);
  const totalDerog = (infraCont?.derogada ? 1 : 0) + (infraContenido?.derogada ? 1 : 0);
  const fijado = value !== "" && value !== null && Number(value) > 0;

  return (
    <section className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-xl p-4" data-testid="prex-derogacion">
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Sparkle className="w-4 h-4 text-amber-700" weight="fill" />
            <h3 className="font-display font-bold text-sm text-amber-900 tracking-tight">
              Derogación de la regla proporcional
            </h3>
          </div>
          <p className="text-[12px] text-slate-700 leading-relaxed">
            Algunas pólizas <b>derogan parcialmente</b> la regla proporcional por infraseguro.
            Si tu cláusula indica, por ejemplo, que <b>se renuncia a aplicar infraseguro hasta
            un déficit del 20%</b>, fíjalo aquí.
          </p>
          <p className="text-[11px] text-muted mt-1.5 leading-relaxed">
            <b>Lógica:</b> si el déficit detectado (capital declarado vs. valor prexistente)
            es <b>menor o igual</b> que este %, la regla queda derogada y el siniestro se indemniza al
            100%. Si el déficit lo <b>supera</b>, sí hay infraseguro y se aplica la regla proporcional
            sobre la valoración del continente.
          </p>
        </div>
        <div className="md:w-56 flex-shrink-0">
          <label className="text-[10px] uppercase tracking-widest font-bold text-amber-800 block mb-1">
            % derogación
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="100"
              step="1"
              value={v}
              onChange={(e) => setV(e.target.value)}
              onBlur={commit}
              placeholder="ej. 20"
              data-testid="prex-derogacion-input"
              className="flex-1 border border-amber-300 rounded-md px-3 py-2 text-sm tabular-nums text-right bg-white focus:outline-none focus:ring-2 focus:ring-amber-300"
            />
            <span className="text-xs text-amber-800 font-semibold">%</span>
            {saving && <span className="text-[10px] text-muted">…</span>}
          </div>
          {fijado && (
            <div className="mt-2 text-[11px] leading-snug">
              {totalDerog > 0 && totalDef === 0 && (
                <span className="text-emerald-700 font-semibold">
                  ✓ Derogada: el infraseguro detectado entra dentro del margen pactado.
                </span>
              )}
              {totalDef > 0 && (
                <span className="text-red-700 font-semibold">
                  ⚠ Infraseguro supera la derogación pactada → se aplica regla proporcional.
                </span>
              )}
              {totalDerog === 0 && totalDef === 0 && (
                <span className="text-muted">Sin déficit que aplicar.</span>
              )}
            </div>
          )}
          {!fijado && (
            <div className="mt-2 text-[11px] text-muted leading-snug">
              Si la póliza no incluye derogación, deja este campo vacío.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}